import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { LoadingSkeleton, Selector, useQueryFetch } from 'sonata-react-components';
import { getCampaignPlan, getCampaignPlans } from 'sonata-static-storage/lib/entities';
import { CampaignPlan } from 'sonata-static-storage/lib/schemas/multiFlight/campaignPlan.schema';

import { useGlobalStore } from '@/store/global';
import { getSessionByKeys } from '@/store/global/session/selectors.ts';

import type { MediaAdvertiserUserInterface } from 'sonata-static-storage/lib/schemas/mediaInfo/mediaAdvertiserUser.schema';

type Props = {
  advertiserId?: MediaAdvertiserUserInterface['_id'];
  id: string;
  disabled?: boolean;
  onChange: (campaignPlan: CampaignPlan) => void;
};

type State = {
  campaignPlanSelected?: CampaignPlan;
};

export function CampaignPlanSelector({ advertiserId, disabled, id, onChange }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { user: userId = '' } = useGlobalStore(store => getSessionByKeys(store, ['user']));
  const token: Record<string, any> = {};
  const [{ campaignPlanSelected }, setState] = useState<State>({ campaignPlanSelected: undefined });

  const { data: campaignPlans, isLoading } = useQueryFetch(
    ['campaignPlanSelector', advertiserId],
    async () =>
      getCampaignPlans(
        userId,
        {
          ...(advertiserId && { advertiserIds: [advertiserId] }),
          fields: ['_id', 'name'],
          sortBy: 'updated',
          sortOrder: 'desc',
          unlimited: true
        },
        token
      ),
    {
      enabled: true
    }
  );

  const campaignList: CampaignPlan[] | undefined = campaignPlans?.items;

  const { data: campaignData } = useQueryFetch(
    ['campaignPlanSelector', campaignPlanSelected?._id],
    async () => getCampaignPlan(userId, campaignPlanSelected?._id as string, token),
    {
      enabled: !!campaignPlanSelected,
      refetchOnWindowFocus: false
    }
  );

  useEffect(() => {
    if (campaignData) {
      onChange(campaignData);
    }
  }, [campaignData]);

  const handleCampaignPlanChange = (value: { id: string; label: string }) => {
    setState(prevState => ({
      ...prevState,
      campaignPlanSelected: campaignList?.find(c => c._id === value.id)
    }));
  };

  if (isLoading || !campaignList) {
    return <LoadingSkeleton className="campaignPlanSelector" height={40} width="100%" />;
  }

  return (
    <Selector
      className="campaignPlanSelector"
      id={id}
      isDisabled={disabled}
      label={formatMessage({
        defaultMessage: 'Campaign Plan:',
        id: 'campaignPlans.modal.campaignPlanSelectorLabel'
      })}
      onChange={handleCampaignPlanChange}
      options={campaignList.map(c => ({
        id: c._id as string,
        label: c.name
      }))}
      placeholder={formatMessage({
        defaultMessage: 'Select Campaign Plan',
        id: 'campaignPlans.campaignPlanSelector.placeholder'
      })}
      type="simple"
    />
  );
}
